import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Chip, Container, Divider, Paper, Stack, Typography } from "@mui/material";
import { CancelOutlined, CampaignOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import useProjectLeads from "../../hooks/projectLeads/useProjectLeads";
import { ProjectLeadDto, ProjectLeadStatus } from "../../types/projectLeadTypes";
import LoadingCircle from "../../components/general/LoadingCircle";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import DiscardProjectLeadDialog from "../../components/projectLeads/DiscardProjectLeadDialog";

/**
 * Detail view for a single Lead.
 *
 * HoCR (permission 19) sees two extra buttons:
 *   - "Projektdaten erfassen" → routes to /projekte/projektausschreibung?fromLead=<id>
 *   - "Lead verwerfen" → opens dialog with mandatory Begründung
 */
const ProjectLeadDetails = () => {
  const { id } = useParams();
  const leadId = Number(id);
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const hasHoCRPermission = doesPermissionsHaveSomeOf(auth.permissions, [19]);

  const { fetchProjectLead, discardProjectLead } = useProjectLeads();
  const [projectLead, setProjectLead] = useState<ProjectLeadDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [discardOpen, setDiscardOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!Number.isFinite(leadId)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchProjectLead(leadId)
      .then((data) => {
        if (!cancelled) setProjectLead(data);
      })
      .catch(() => {
        if (!cancelled) setProjectLead(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchProjectLead, leadId]);

  const handleDiscard = async (reason: string) => {
    const updated = await discardProjectLead(leadId, { reason });
    setProjectLead(updated);
  };

  const renderStatusChip = (status: ProjectLeadDto["status"]) => {
    switch (status) {
      case ProjectLeadStatus.Lead:
        return <Chip label="Lead" color="info" />;
      case ProjectLeadStatus.Discarded:
        return <Chip label="Lead verworfen" color="error" variant="outlined" />;
      case ProjectLeadStatus.Converted:
        return <Chip label="Ausgeschrieben" color="success" />;
      default:
        return <Chip label={status} />;
    }
  };

  if (loading) return <LoadingCircle />;
  if (!projectLead) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">Lead nicht gefunden oder keine Berechtigung.</Alert>
      </Container>
    );
  }

  const canAct = hasHoCRPermission && projectLead.status === ProjectLeadStatus.Lead;

  return (
    <Container maxWidth="md">
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ flexGrow: 1 }}>
          {projectLead.projectName}
        </Typography>
        {renderStatusChip(projectLead.status)}
      </Stack>

      <Paper sx={{ paddingX: 4, paddingY: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Angelegt von
            </Typography>
            <Typography>
              {projectLead.creatorName ?? `Member #${projectLead.creatorId}`} ·{" "}
              {dayjs(projectLead.createdAt).format("DD.MM.YYYY HH:mm")}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary">
              Ausgangssituation / Zielsetzung
            </Typography>
            <Typography whiteSpace="pre-wrap">{projectLead.situation || "—"}</Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Zeitraum
              </Typography>
              <Typography>{projectLead.timeframe || "—"}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Umsatz
              </Typography>
              <Typography>{projectLead.revenue || "—"}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Kunde
              </Typography>
              <Typography>{projectLead.customer || "—"}</Typography>
            </Box>
          </Stack>

          {projectLead.status === ProjectLeadStatus.Discarded && projectLead.discardReason && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Begründung (verworfen)
                </Typography>
                <Typography whiteSpace="pre-wrap">{projectLead.discardReason}</Typography>
              </Box>
            </>
          )}

          {projectLead.status === ProjectLeadStatus.Converted && projectLead.projectId && (
            <>
              <Divider />
              <Alert severity="success">
                Dieser Lead wurde ausgeschrieben.{" "}
                <RouterLink to={`/projekte/${projectLead.projectId}`}>Zum Projekt</RouterLink>
              </Alert>
            </>
          )}
        </Stack>
      </Paper>

      {canAct && (
        <Box sx={{ display: "flex", gap: 2, mt: 3, justifyContent: "flex-end" }}>
          <Button color="error" variant="outlined" startIcon={<CancelOutlined />} onClick={() => setDiscardOpen(true)}>
            Lead verwerfen
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<CampaignOutlined />}
            onClick={() => navigate(`/projekte/projektausschreibung?fromLead=${projectLead.leadId}`)}
          >
            Projektdaten erfassen
          </Button>
        </Box>
      )}

      <DiscardProjectLeadDialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        onConfirm={handleDiscard}
        projectName={projectLead.projectName}
      />
    </Container>
  );
};

export default ProjectLeadDetails;
